import { Component, Input, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { PoModalAction, PoModalComponent, PoNotificationService } from '@po-ui/ng-components';
import { PgcCustomButtonsService } from '../../services/pgc-custom-buttons.service';
import { LiteralsService } from '../../services/literals-service';
import { LiteralsWords } from 'src/app/i18n/literals.model';
import {
  PgcCustomButton,
  PgcCustomButtonLocation,
  PgcCustomButtonTiming,
  PgcCustomButtonActionPayload,
  PgcCustomButtonActionResponse,
  PgcCustomButtonFilter
} from '../../models/pgc-custom-buttons.model';

@Component({
  selector: 'pgc-custom-buttons',
  templateUrl: './pgc-custom-buttons.component.html',
  styleUrls: ['./pgc-custom-buttons.component.css'],
  standalone: false
})
export class PgcCustomButtonsComponent implements OnInit {

  literals: LiteralsWords = this.literalsService.getLiterals();

  /** Local predefinido onde os botões devem ser exibidos */
  @Input() location: PgcCustomButtonLocation;

  /** Momento de execução: before (modal antes da listagem) ou after (inline após listagem) */
  @Input() timing: PgcCustomButtonTiming = PgcCustomButtonTiming.AFTER;

  /** Título do modal (usado no modo before) */
  @Input() modalTitle: string = this.literals?.str0914;

  /** Contexto da operação atual (dados da cotação, SC, etc.) a ser enviado ao backend */
  @Input() context: Record<string, any> = {};

  /** Evento emitido após a execução de uma ação com sucesso (retorna a resposta do backend) */
  @Output() actionExecuted = new EventEmitter<PgcCustomButtonActionResponse>();

  /** Evento emitido quando a execução de uma ação falha */
  @Output() actionFailed = new EventEmitter<PgcCustomButtonActionResponse>();

  /**
   * Evento emitido no modo before com os filtros retornados pelo PE.
   * A tela pai deve capturar este evento para aplicar os filtros na listagem.
   */
  @Output() filtersApplied = new EventEmitter<PgcCustomButtonFilter>();

  /**
   * Evento emitido no modo before quando o usuário fecha o modal sem selecionar nenhuma opção.
   * Permite que a tela pai carregue a listagem padrão (sem filtros customizados).
   */
  @Output() beforeSkipped = new EventEmitter<void>();

  @ViewChild('customButtonsModal', { static: false }) customButtonsModal: PoModalComponent;

  buttons: PgcCustomButton[] = [];
  loading: boolean = false;
  executingAction: string = '';

  /** Indica se o modal before já foi exibido (para não reabrir ao navegar) */
  modalShown: boolean = false;

  /** Ação secundária do modal (Pular / Carregar sem filtro) */
  secondaryModalAction: PoModalAction = {
    action: () => this.skipBeforeModal(),
    label: this.literals?.str0915
  };

  constructor(
    private customButtonsService: PgcCustomButtonsService,
    private poNotification: PoNotificationService,
    private literalsService: LiteralsService,

  ) {}

  ngOnInit(): void {
    this.loadButtons();
  }

  /** Busca os botões configurados para a localização e timing informados */
  loadButtons(): void {
    this.loading = true;
    this.customButtonsService.getButtons(this.location, this.timing).subscribe({
      next: (response) => {
        this.buttons = response?.buttons ?? [];
        this.loading = false;

        // Se timing=before e existem botões, abre o modal automaticamente
        if (this.timing === PgcCustomButtonTiming.BEFORE && this.buttons.length > 0 && !this.modalShown) {
          this.modalShown = true;
          // Timeout para garantir que o ViewChild esteja disponível
          setTimeout(() => this.customButtonsModal?.open(), 0);
        }

        // Se timing=before mas não há botões, emite skip para carregar listagem normalmente
        if (this.timing === PgcCustomButtonTiming.BEFORE && this.buttons.length === 0) {
          this.beforeSkipped.emit();
        }
      },
      error: () => {
        // Falha silenciosa — se não houver ponto de entrada configurado, não exibe botões
        this.buttons = [];
        this.loading = false;

        // Se timing=before e falhou, emite skip para carregar normalmente
        if (this.timing === PgcCustomButtonTiming.BEFORE) {
          this.beforeSkipped.emit();
        }
      }
    });
  }

  /** Executa a ação associada ao botão no backend */
  executeButtonAction(button: PgcCustomButton): void {
    if (this.executingAction) {
      return; // Evita execuções simultâneas
    }

    this.executingAction = button.id;

    const payload: PgcCustomButtonActionPayload = {
      action: button.action,
      location: this.location,
      timing: this.timing,
      context: this.context
    };

    this.customButtonsService.executeAction(payload).subscribe({
      next: (response: PgcCustomButtonActionResponse) => {
        this.executingAction = '';

        if (response.success) {
          this.poNotification.success(response.message);

          // No modo before, fecha o modal e emite os filtros retornados
          if (this.timing === PgcCustomButtonTiming.BEFORE) {
            this.customButtonsModal?.close();

            if (response.filters) {
              this.filtersApplied.emit(response.filters);
            } else {
              // Ação executada com sucesso mas sem filtros — carrega listagem padrão
              this.beforeSkipped.emit();
            }
          }

          this.actionExecuted.emit(response);
        } else {
          this.poNotification.error(response.message);
          this.actionFailed.emit(response);
        }
      },
      error: (error) => {
        this.executingAction = '';
        const errorMessage = error?.error?.message || error?.message || this.literals?.str0916;
        this.poNotification.error(errorMessage);
        this.actionFailed.emit({ success: false, message: errorMessage, timing: this.timing } as any);
      }
    });
  }

  /** Verifica se um botão específico está em execução */
  isExecuting(button: PgcCustomButton): boolean {
    return this.executingAction === button.id;
  }

  /** Fecha o modal before sem executar nenhuma ação — carrega listagem padrão */
  skipBeforeModal(): void {
    this.customButtonsModal?.close();
    this.beforeSkipped.emit();
  }
}
